import { useEffect, useRef, useState } from 'react'
import { Animated, View } from 'react-native'
import type {
  LayoutChangeEvent,
  LayoutRectangle,
  ViewStyle,
} from 'react-native'
import Point from '@ca/ui/features/measurement/Points/Point'
import type { PointProps } from '@ca/ui/features/measurement/Points/types'
import type {
  OpticalMeasureOverlayData,
  OpticalMeasureOverlayPoint,
  OpticalMeasureOverlayPointKind,
} from '../../index'

type Props = {
  data?: OpticalMeasureOverlayData
  isVisible: boolean
  visibleKinds: ReadonlySet<OpticalMeasureOverlayPointKind>
  /** Per-point variant. Every point renders as `auto` when omitted. */
  resolveVariant?: (point: OpticalMeasureOverlayPoint) => PointProps['variant']
}

const POINT_FADE_DURATION_MS = 180
const POINT_MOVE_DURATION_MS = 140
const REVIEW_READY_FALLBACK_DELAY_MS = 250

type DotProps = {
  x: number
  y: number
  /** Native's own transition for this update; 0 for an immediate one. */
  moveDurationMs: number
  type: PointProps['type']
  variant: PointProps['variant']
}

function AnimatedPoint({ x, y, moveDurationMs, type, variant }: DotProps) {
  const opacity = useRef(new Animated.Value(0)).current
  const position = useRef(new Animated.ValueXY({ x, y })).current
  const hasArrivedRef = useRef(false)

  useEffect(() => {
    Animated.timing(opacity, {
      toValue: 1,
      duration: POINT_FADE_DURATION_MS,
      useNativeDriver: true,
    }).start()
  }, [opacity])

  useEffect(() => {
    // The first coordinates are where the point appears, not somewhere it
    // moved to — sliding in from the initial value would be a lie.
    if (!hasArrivedRef.current) {
      hasArrivedRef.current = true
      position.setValue({ x, y })
      return
    }

    // Manual adjustment nudges these a fraction of a millimeter at a time, so
    // every frozen-frame rebuild lands a new position on an existing point.
    // Animating the move keeps that continuous instead of restarting the fade.
    Animated.timing(position, {
      toValue: { x, y },
      duration: moveDurationMs > 0 ? moveDurationMs : POINT_MOVE_DURATION_MS,
      useNativeDriver: true,
    }).start()
  }, [moveDurationMs, position, x, y])

  const style: Animated.WithAnimatedObject<ViewStyle> = {
    position: 'absolute',
    opacity,
    transform: position.getTranslateTransform(),
  }

  return (
    <Animated.View style={style}>
      <Point type={type} variant={variant} />
    </Animated.View>
  )
}

const FRAME_POINT_KINDS = new Set<OpticalMeasureOverlayPointKind>([
  'left-lens-bottom',
  'right-lens-bottom',
  'left-lens-top',
  'right-lens-top',
])

const getPointType = (point: OpticalMeasureOverlayPoint): PointProps['type'] =>
  FRAME_POINT_KINDS.has(point.kind) ? 'frame' : 'eye'

export function MeasurementPointOverlay({
  data,
  isVisible,
  visibleKinds,
  resolveVariant,
}: Props) {
  const [layout, setLayout] = useState<LayoutRectangle>()
  const [visibleData, setVisibleData] = useState<OpticalMeasureOverlayData>()
  const hasRevealedRef = useRef(false)
  const revealAtRef = useRef(0)

  const handleLayout = (event: LayoutChangeEvent) => {
    setLayout(event.nativeEvent.layout)
  }

  useEffect(() => {
    if (!isVisible || !data) {
      hasRevealedRef.current = false
      revealAtRef.current = 0
      setVisibleData(undefined)
      return
    }

    if (hasRevealedRef.current) {
      setVisibleData(data)
      return
    }

    const now = Date.now()
    const revealDelay =
      data.animationDurationMs > 0
        ? data.animationDurationMs
        : REVIEW_READY_FALLBACK_DELAY_MS
    if (data.animationDurationMs > 0 || revealAtRef.current === 0) {
      revealAtRef.current = Math.max(revealAtRef.current, now + revealDelay)
    }

    const timeout = setTimeout(() => {
      hasRevealedRef.current = true
      setVisibleData(data)
    }, Math.max(0, revealAtRef.current - now))

    return () => clearTimeout(timeout)
  }, [data, isVisible])

  const viewport = visibleData?.viewport
  const scaleX = layout && viewport?.width ? layout.width / viewport.width : 1
  const scaleY =
    layout && viewport?.height ? layout.height / viewport.height : 1
  const points =
    (isVisible &&
      visibleData?.groups.flatMap((group) =>
        group.points
          .filter((point) => visibleKinds.has(point.kind))
          .map((point) => ({
            ...point,
            renderKey: `${group.key}:${point.id}`,
          }))
      )) ||
    []

  return (
    <View
      pointerEvents="none"
      className="absolute inset-0"
      onLayout={handleLayout}
    >
      {points.map((point) => (
        <AnimatedPoint
          key={point.renderKey}
          x={point.x * scaleX}
          y={point.y * scaleY}
          moveDurationMs={visibleData?.animationDurationMs ?? 0}
          type={getPointType(point)}
          variant={resolveVariant?.(point) ?? 'auto'}
        />
      ))}
    </View>
  )
}
